import { Formik, Form } from 'formik';
import { PropTypes } from 'prop-types';
import React from 'react';
import * as Yup from 'yup';

import { Connections } from './connections';
import { Products } from './products';
import { Space } from './space';
import ButtonOutlineWithStates from '@atoms/ButtonOutlineWithStates';
import SubmitButton from '@atoms/SubmitButton';
import LoadingInline from '@molecules/LoadingInline';
import { BodyLarge, DividerLightGreyStyled, H4, LabelSmall } from '@styled';

const StepTwo = ({ data, loading, submitButtonClass = '', onNext, onBack }) => {
    const content = {
        TENANTS: {
            title: "Tell us more about what kind of space you're offering so we can give you the best matches.",
            form: Space, // Same questions for both Tenants and Space
        },
        SPACE: {
            title: "Tell us more about what kind of space you're looking for so we can give you the best matches.",
            form: Space,
        },
        CONNECTIONS: {
            title: 'Tell us more about what you’re looking for or providing so we can give you the best matches.',
            form: Connections,
        },
        PRODUCTS: {
            title: 'Tell us more about what you’re looking for or providing so we can give you the best matches.',
            form: Products,
        },
    };

    const FormComponent = content[data.primaryInterest].form;

    return (
        <div>
            <div className="mb-1rem">
                <LabelSmall>Step 2 of 3</LabelSmall>
            </div>

            <H4 className="mb-3">Tell us more about what you’re looking for.</H4>
            <DividerLightGreyStyled className="mt-0.5rem mb-2rem" />
            <BodyLarge className="mb-2rem">{content[data.primaryInterest].title}</BodyLarge>

            {loading || !data ? (
                <LoadingInline />
            ) : (
                <Formik onSubmit={onNext} initialValues={data} validationSchema={validationSchema}>
                    {({ values, errors, isValid, submitCount }) => (
                        <Form errors={errors}>
                            {!isValid && submitCount > 0 && (
                                <div className="alert alert-danger">There were issues with your submission.</div>
                            )}
                            <FormComponent kind={data.primaryInterest.toLowerCase()} values={values} />

                            <div className="d-md-flex">
                                <ButtonOutlineWithStates buttonClasses="mr-2" onClick={onBack}>
                                    Back to Previous
                                </ButtonOutlineWithStates>

                                <SubmitButton buttonClasses={submitButtonClass} isDisabled={!isValid}>
                                    Save & Proceed
                                </SubmitButton>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}
        </div>
    );
};

StepTwo.propTypes = {
    data: PropTypes.object,
    loading: PropTypes.bool,
    submitButtonClass: PropTypes.string,
    onNext: PropTypes.func,
    onBack: PropTypes.func,
};

const validationSchema = Yup.object().shape({
    connections: Yup.object().when('primaryInterest', {
        is: primaryInterest => primaryInterest === 'CONNECTIONS',
        then: Yup.object({
            connectionType: Yup.string()
                .nullable()
                .required('Please tell us more about who you are looking to connect with.'),
        }),
    }),
    products: Yup.object().when('primaryInterest', {
        is: primaryInterest => primaryInterest === 'PRODUCTS',
        then: Yup.object({
            areYou: Yup.string()
                .nullable()
                .required('Please tell us about yourself.'),
            productTypes: Yup.string()
                .nullable()
                .required('Please select at least one product type.'),
        }),
    }),
    tenants: Yup.object().when('primaryInterest', {
        is: primaryInterest => primaryInterest === 'TENANTS',
        then: Yup.object({
            purpose: Yup.array()
                .of(Yup.string())
                .required('Please select a preferred purpose.'),
            density: Yup.array()
                .of(Yup.string())
                .required('Please select a population density.'),
            size: Yup.array()
                .of(Yup.string())
                .required('Please select a size.'),
            typeOfCenter: Yup.array()
                .of(Yup.string())
                .required('Please select a type of center.'),
            typeOfFacility: Yup.array()
                .of(Yup.string())
                .required('Please select a type of facility.'),
        }),
    }),
    space: Yup.object().when('primaryInterest', {
        is: primaryInterest => primaryInterest === 'SPACE',
        then: Yup.object({
            purpose: Yup.array()
                .of(Yup.string())
                .required('Please select a preferred purpose.'),
            density: Yup.array()
                .of(Yup.string())
                .required('Please select a population density.'),
            size: Yup.array()
                .of(Yup.string())
                .required('Please select a size.'),
            typeOfCenter: Yup.array()
                .of(Yup.string())
                .required('Please select a type of center.'),
            typeOfFacility: Yup.array()
                .of(Yup.string())
                .required('Please select a type of facility.'),
        }),
    }),
});

export default StepTwo;
